import { AddOutlined } from '@mui/icons-material';
import { Box, Button, ThemeProvider } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import NoteEditor from '../components/noteEditor';
import NotesList from '../components/notesList';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import PageHeader from 'components/v2/common/pageHeader';

import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import { useCreateNote, useDeleteNotes, useNotesByCase, useUpdateNote } from 'hooks/useNotes';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { NoteFormInterface, NoteInterface } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { noteFormDefaultValues } from 'utils/validations/notes';

/** Payload stored on modalObject.data for notes CRUD (create/edit/delete/bulk delete). */
export interface NotesModalData {
    note?: NoteInterface | null;
    noteIds?: number[];
}

const ClientNotesV2: React.FC = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const appId = activeApp?.id;

    const {
        data: notes = [],
        isLoading,
        isError,
        error,
    } = useNotesByCase(appId, { enabled: Boolean(appId) });

    const createNote = useCreateNote(appId);
    const updateNote = useUpdateNote(appId);
    const deleteNotes = useDeleteNotes(appId);

    const { modalObject, setModalObject, onResetModalObj } = useModalObj<NotesModalData>();

    useEffect(() => {
        if (isError) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('notesLoadError')),
                    type: 'error',
                })
            );
        }
    }, [isError, error, dispatch, translate]);

    const handleAction = (action: ACTIONS, note?: NoteInterface) => {
        switch (action) {
            case ACTIONS.CREATE:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate('addNote'),
                    content: '',
                    data: null,
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.EDIT:
                if (!note) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate('editNote'),
                    content: '',
                    data: { note },
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.DELETE:
                if (!note) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate('deleteNote'),
                    content: translate('deleteNoteConfirm'),
                    data: { note, noteIds: [note.id] },
                    disabled: false,
                    loading: false,
                }));
                break;
            default:
                break;
        }
    };

    const handleDeleteSelected = (noteIds: number[]) => {
        if (!noteIds.length) return;

        const isSingle = noteIds.length === 1;
        const singleNote = isSingle ? notes.find((n) => n.id === noteIds[0]) : undefined;

        setModalObject((prev) => ({
            ...prev,
            action: ACTIONS.DELETE,
            title: translate(isSingle ? 'deleteNote' : 'deleteSelectedNotes'),
            content: isSingle
                ? translate('deleteNoteConfirm')
                : translate('deleteNotesConfirm').replace('{{count}}', String(noteIds.length)),
            data: {
                note: singleNote ?? null,
                noteIds,
            },
            disabled: false,
            loading: false,
        }));
    };

    const handleCloseModal = () => {
        if (modalObject.loading) return;
        onResetModalObj();
    };

    const handleSubmit = async (values: NoteFormInterface) => {
        if (!activeApp) return;

        const isEdit = modalObject.action === ACTIONS.EDIT;
        const note = modalObject.data?.note;

        setModalObject((prev) => ({ ...prev, disabled: true, loading: true }));

        try {
            if (isEdit && note) {
                await updateNote.mutateAsync({
                    id: note.id,
                    title: values.title.trim(),
                    content: values.content.trim(),
                });
            } else {
                await createNote.mutateAsync({
                    application_id: activeApp.id,
                    candidate_id: activeApp.candidate_id,
                    title: values.title.trim(),
                    content: values.content.trim(),
                });
            }

            dispatch(setAlert({ message: translate('noteSaved'), type: 'success' }));
            onResetModalObj();
        } catch (err) {
            setModalObject((prev) => ({ ...prev, disabled: false, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(err, translate('notesSaveError')),
                    type: 'error',
                })
            );
        }
    };

    const handleConfirmDelete = async () => {
        const noteIds = modalObject.data?.noteIds ?? [];
        if (!noteIds.length) return;

        setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));

        try {
            await deleteNotes.mutateAsync(noteIds);
            dispatch(setAlert({ message: translate('notesDeleted'), type: 'success' }));
            onResetModalObj();
        } catch (err) {
            setModalObject((prev) => ({ ...prev, loading: false, disabled: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(err, translate('notesDeleteError')),
                    type: 'error',
                })
            );
        }
    };

    const isEditorOpen =
        modalObject.action === ACTIONS.CREATE || modalObject.action === ACTIONS.EDIT;

    const editorInitialValues: NoteFormInterface =
        modalObject.action === ACTIONS.EDIT && modalObject.data?.note
            ? {
                  title: modalObject.data.note.title,
                  content: modalObject.data.note.content,
              }
            : noteFormDefaultValues;

    const formLoading = modalObject.loading || createNote.isPending || updateNote.isPending;
    const deleteLoading = modalObject.loading || deleteNotes.isPending;

    return (
        <ThemeProvider theme={appThemeV2}>
            <Box sx={{ width: '100%', px: { xs: 2, md: 0 } }}>
                <PageHeader title="notes" description="notesDescription">
                    <Button
                        variant="contained"
                        startIcon={<AddOutlined />}
                        onClick={() => handleAction(ACTIONS.CREATE)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {translate('addNote')}
                    </Button>
                </PageHeader>

                <NotesList
                    notes={notes}
                    loading={isLoading}
                    handleAction={handleAction}
                    onDeleteSelected={handleDeleteSelected}
                    deleting={deleteLoading}
                />

                {isEditorOpen ? (
                    <NoteEditor
                        open
                        mode={modalObject.action === ACTIONS.EDIT ? 'edit' : 'create'}
                        noteId={modalObject.data?.note?.id}
                        initialValues={editorInitialValues}
                        loading={formLoading}
                        onClose={handleCloseModal}
                        onSubmit={handleSubmit}
                    />
                ) : null}

                {modalObject.action === ACTIONS.DELETE ? (
                    <ConfirmationDialog
                        title={modalObject.title}
                        content={modalObject.content}
                        loading={deleteLoading}
                        onClose={handleCloseModal}
                        onConfirm={handleConfirmDelete}
                    />
                ) : null}
            </Box>
        </ThemeProvider>
    );
};

export default ClientNotesV2;
